<template>
  <v-app>
    <v-app-bar :elevation="1" color="surface" height="70" class="px-md-10">
      <template v-slot:prepend>
        <div class="d-flex align-center text-primary mr-8 cursor-pointer">
          <v-sheet
            color="primary"
            rounded="lg"
            width="32"
            height="32"
            class="d-flex justify-center align-center mr-3"
          >
            <v-icon color="white" size="20">mdi-trophy-variant</v-icon>
          </v-sheet>
          <span class="text-h6 font-weight-black tracking-tight text-high-emphasis">
            Sports Portal
          </span>
        </div>
      </template>

      <div class="d-none d-md-flex gap-4">
        <v-btn variant="text" class="text-body-2 font-weight-bold" color="grey-darken-3">ショップ検索</v-btn>
        <v-btn variant="text" class="text-body-2 font-weight-bold" color="grey-darken-3">カテゴリー</v-btn>
        <v-btn variant="text" class="text-body-2 font-weight-bold" color="grey-darken-3">特集</v-btn>
        <v-btn variant="text" class="text-body-2 font-weight-bold" color="grey-darken-3">ショップ登録</v-btn>
      </div>

      <v-spacer></v-spacer>

      <div class="d-none d-sm-flex align-center" style="max-width: 300px; width: 100%;">
        <v-text-field
          density="compact"
          variant="solo-filled"
          flat
          hide-details
          placeholder="ショップ名・地域・競技"
          prepend-inner-icon="mdi-magnify"
          bg-color="grey-lighten-4"
          rounded="lg"
          class="mr-4"
        ></v-text-field>
      </div>

      <div class="d-flex gap-2">
        <v-btn color="primary" variant="flat" class="font-weight-bold" rounded="lg">
          ログイン
        </v-btn>
        <v-btn
          color="grey-darken-3"
          variant="tonal"
          class="font-weight-bold d-none d-sm-flex"
          rounded="lg"
        >
          新規登録
        </v-btn>
      </div>
    </v-app-bar>

    <v-main class="bg-grey-lighten-5">
      <v-container class="py-10">
        <v-sheet
          border
          rounded="xl"
          class="overflow-hidden"
          elevation="0"
        >
          <v-row no-gutters>
            <v-col cols="12" md="6">
              <v-img
                src="https://images.unsplash.com/photo-1461896836934-ffe607ba8211?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                cover
                height="100%"
                min-height="300"
                alt="Professional athlete"
              ></v-img>
            </v-col>
            
            <v-col cols="12" md="6" class="d-flex flex-column justify-center p-8 pa-md-12">
              <div class="text-left">
                <v-chip
                  color="primary"
                  variant="tonal"
                  label
                  size="small"
                  class="font-weight-bold mb-4 text-uppercase letter-spacing-2"
                >
                  Local Sports Finder
                </v-chip>
                
                <h1 class="text-h4 text-md-h3 font-weight-black mb-6 text-high-emphasis lh-tight">
                  あなたの街のスポーツショップを、もっと身近に。
                </h1>
                
                <p class="text-body-1 text-grey-darken-1 mb-8">
                  最新のギア、地元の名店。あなたのスポーツライフを支える一軒がここに見つかる。1,000店以上のショップから検索可能。
                </p>
                
                <div class="d-flex flex-column flex-sm-row gap-3">
                  <v-btn
                    color="primary"
                    size="x-large"
                    elevation="4"
                    class="font-weight-bold px-6"
                    rounded="lg"
                  >
                    近くのショップを見つける
                  </v-btn>
                  <v-btn
                    variant="outlined"
                    color="primary"
                    size="x-large"
                    class="font-weight-bold px-6"
                    rounded="lg"
                  >
                    人気の競技から探す
                  </v-btn>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-sheet>
      </v-container>

      <v-container class="py-5">
        <div class="d-flex justify-space-between align-center mb-4 px-2">
          <div class="d-flex align-center">
            <v-sheet color="primary" width="4" height="32" rounded="pill" class="mr-3"></v-sheet>
            <h2 class="text-h5 font-weight-bold text-high-emphasis">カテゴリーから探す</h2>
          </div>
          <a href="#" class="text-primary text-body-2 font-weight-bold text-decoration-none hover:text-decoration-underline">すべて見る</a>
        </div>

        <v-row dense>
          <v-col
            v-for="(cat, index) in categories"
            :key="index"
            cols="auto"
          >
            <v-card
              link
              flat
              border
              class="d-flex align-center px-4 py-2 rounded-lg hover-card"
              min-height="48"
            >
              <v-icon :icon="cat.icon" color="primary" class="mr-2"></v-icon>
              <span class="text-body-2 font-weight-bold">{{ cat.name }}</span>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <v-container class="py-5">
        <div class="d-flex justify-space-between align-center mb-4 px-2 pt-4">
          <div class="d-flex align-center gap-4">
            <div class="d-flex align-center">
              <v-sheet color="primary" width="4" height="32" rounded="pill" class="mr-3"></v-sheet>
              <h2 class="text-h5 font-weight-bold text-high-emphasis">注目のショップ</h2>
            </div>
            <p class="d-none d-sm-block text-body-2 text-grey mb-0 align-self-end pb-1">現在地周辺で人気のショップ</p>
          </div>
        </div>

        <v-row>
          <v-col
            v-for="(shop, i) in shops"
            :key="i"
            cols="12"
            md="6"
            lg="4"
          >
            <v-card
              class="rounded-xl mx-auto h-100 d-flex flex-column group"
              elevation="1"
              hover
            >
              <div class="position-relative">
                <v-img
                  :src="shop.image"
                  height="200"
                  cover
                  class="align-end transition-transform"
                >
                  <template v-slot:placeholder>
                    <v-row class="fill-height ma-0" align="center" justify="center">
                      <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </v-row>
                  </template>
                </v-img>
                <v-chip
                  v-if="shop.badge"
                  class="position-absolute top-0 right-0 ma-3 font-weight-bold"
                  color="white"
                  variant="flat"
                  size="small"
                  label
                >
                  <span class="text-primary">{{ shop.badge }}</span>
                </v-chip>
              </div>

              <v-card-text class="flex-grow-1 pt-4">
                <div class="d-flex justify-space-between align-start mb-2">
                  <v-chip
                    size="x-small"
                    color="primary"
                    variant="tonal"
                    label
                    class="font-weight-bold"
                  >
                    {{ shop.category }}
                  </v-chip>
                  <div class="d-flex align-center text-amber-darken-2">
                    <v-icon icon="mdi-star" size="small" class="mr-1"></v-icon>
                    <span class="text-caption font-weight-bold">{{ shop.rating }}</span>
                  </div>
                </div>

                <h3 class="text-h6 font-weight-bold mb-2 text-high-emphasis text-truncate">
                  {{ shop.name }}
                </h3>
                <p class="text-body-2 text-grey-darken-1 mb-3 line-clamp-2">
                  {{ shop.description }}
                </p>

                <v-divider class="mb-3"></v-divider>

                <div class="d-flex align-center text-caption text-grey mb-4">
                  <v-icon icon="mdi-map-marker" size="small" class="mr-1"></v-icon>
                  {{ shop.address }}
                </div>
              </v-card-text>

              <v-card-actions class="px-4 pb-4 pt-0">
                <v-btn
                  block
                  variant="flat"
                  color="grey-lighten-4"
                  class="font-weight-bold text-body-2 hover-primary"
                  height="40"
                  rounded="lg"
                >
                  店舗詳細を見る
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <v-container class="py-10">
        <v-sheet
          color="#221010"
          rounded="xl"
          class="pa-8 pa-lg-16 position-relative overflow-hidden"
        >
          <div class="position-absolute w-100 h-100" style="top:0; left:0; opacity: 0.1; background-image: radial-gradient(white 1px, transparent 0); background-size: 24px 24px;"></div>

          <v-row align="center" class="position-relative z-index-1">
            <v-col cols="12" lg="7" class="text-center text-lg-left">
              <h2 class="text-h4 font-weight-black text-white mb-6">近くのショップをマップで探す</h2>
              <p class="text-body-1 text-grey-lighten-2 mb-8" style="line-height: 1.8;">
                あなたの現在地から最も近いスポーツショップをリアルタイムで表示します。<br class="d-none d-md-block">営業時間や在庫状況も一目でチェック。
              </p>
              <v-btn
                color="primary"
                size="x-large"
                class="font-weight-bold px-8"
                rounded="xl"
                prepend-icon="mdi-map"
              >
                マップを開く
              </v-btn>
            </v-col>

            <v-col cols="12" lg="5" class="d-flex justify-center">
              <v-sheet
                width="100%"
                max-width="400"
                :aspect-ratio="1"
                rounded="xl"
                class="position-relative overflow-hidden border-lg border-opacity-25"
                elevation="10"
                style="border: 4px solid rgba(255,255,255,0.1);"
              >
                <v-img
                  src="https://images.unsplash.com/photo-1524661135-423995f22d0b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  cover
                  class="fill-height grayscale"
                >
                  <div class="fill-height d-flex align-center justify-center" style="background-color: rgba(236, 19, 19, 0.2);">
                    <v-sheet
                      color="primary"
                      width="64"
                      height="64"
                      rounded="circle"
                      elevation="10"
                      class="d-flex align-center justify-center pulse-animation"
                    >
                      <v-icon color="white" size="32">mdi-crosshairs-gps</v-icon>
                    </v-sheet>
                  </div>
                </v-img>
              </v-sheet>
            </v-col>
          </v-row>
        </v-sheet>
      </v-container>
    </v-main>

    <v-footer class="bg-surface pt-12 pb-6 border-t">
      <v-container>
        <v-row>
          <v-col cols="12" md="4" class="mb-6">
            <div class="d-flex align-center text-primary mb-4">
              <v-sheet color="primary" rounded width="24" height="24" class="d-flex justify-center align-center mr-2">
                <v-icon color="white" size="16">mdi-trophy-variant</v-icon>
              </v-sheet>
              <span class="text-h6 font-weight-black tracking-tight text-high-emphasis">Sports Portal</span>
            </div>
            <p class="text-body-2 text-grey-darken-1 mb-4">
              日本全国のスポーツショップ情報を網羅するポータルサイト。スポーツライフをもっと楽しく、もっと快適に。
            </p>
            <div class="d-flex gap-2">
              <v-btn icon="mdi-share-variant" variant="tonal" size="small" color="grey"></v-btn>
              <v-btn icon="mdi-at" variant="tonal" size="small" color="grey"></v-btn>
            </div>
          </v-col>

          <v-col cols="12" sm="4" md="2">
            <h3 class="text-subtitle-1 font-weight-bold mb-4">探す</h3>
            <div class="d-flex flex-column gap-2">
              <a href="#" class="text-caption text-grey-darken-1 text-decoration-none hover-primary">ショップ一覧</a>
              <a href="#" class="text-caption text-grey-darken-1 text-decoration-none hover-primary">競技から探す</a>
              <a href="#" class="text-caption text-grey-darken-1 text-decoration-none hover-primary">地域から探す</a>
              <a href="#" class="text-caption text-grey-darken-1 text-decoration-none hover-primary">最新トレンドギア</a>
            </div>
          </v-col>

          <v-col cols="12" sm="4" md="3">
            <h3 class="text-subtitle-1 font-weight-bold mb-4">ショップの方へ</h3>
             <div class="d-flex flex-column gap-2">
              <a href="#" class="text-caption text-grey-darken-1 text-decoration-none hover-primary">ショップ掲載のご案内</a>
              <a href="#" class="text-caption text-grey-darken-1 text-decoration-none hover-primary">管理画面ログイン</a>
              <a href="#" class="text-caption text-grey-darken-1 text-decoration-none hover-primary">集客支援プラン</a>
              <a href="#" class="text-caption text-grey-darken-1 text-decoration-none hover-primary">成功事例</a>
            </div>
          </v-col>

          <v-col cols="12" sm="4" md="3">
             <h3 class="text-subtitle-1 font-weight-bold mb-4">サポート</h3>
             <div class="d-flex flex-column gap-2">
              <a href="#" class="text-caption text-grey-darken-1 text-decoration-none hover-primary">ヘルプセンター</a>
              <a href="#" class="text-caption text-grey-darken-1 text-decoration-none hover-primary">利用規約</a>
              <a href="#" class="text-caption text-grey-darken-1 text-decoration-none hover-primary">プライバシーポリシー</a>
              <a href="#" class="text-caption text-grey-darken-1 text-decoration-none hover-primary">お問い合わせ</a>
            </div>
          </v-col>
        </v-row>

        <v-divider class="my-8"></v-divider>

        <div class="text-center text-caption text-grey-lighten-1">
          &copy; 2024 Sports Portal. All Rights Reserved.
        </div>
      </v-container>
    </v-footer>
  </v-app>
</template>

<script setup>
import { ref } from 'vue';

// カテゴリーデータ
const categories = ref([
  { name: '野球', icon: 'mdi-baseball' },
  { name: 'サッカー', icon: 'mdi-soccer' },
  { name: 'テニス', icon: 'mdi-tennis' },
  { name: 'ランニング', icon: 'mdi-run' },
  { name: 'バスケットボール', icon: 'mdi-basketball' },
  { name: 'アウトドア', icon: 'mdi-image-filter-hdr' }, // landscape replacement
  { name: '水泳', icon: 'mdi-pool' },
  { name: 'トレーニング', icon: 'mdi-dumbbell' },
]);

// ショップデータ（画像はUnsplashのプレースホルダーを使用）
const shops = ref([
  {
    name: '東京スポーツセンター 銀座店',
    category: '総合スポーツ',
    rating: 4.8,
    description: '都内最大級の品揃えを誇るフラッグシップショップ。あらゆる競技の最新モデルが揃います。',
    address: '東京都中央区銀座 4-1-1',
    image: 'https://images.unsplash.com/photo-1556906781-9a412961c28c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    badge: 'SALE中'
  },
  {
    name: 'Runners Hub 横浜',
    category: 'ランニング',
    rating: 4.9,
    description: 'フォーム診断からシューズ選びまで。ランナーのための専門ブティック。',
    address: '神奈川県横浜市西区 2-10-3',
    image: 'https://images.unsplash.com/photo-1595341888016-a392ef81b7de?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    badge: 'NEW'
  },
  {
    name: 'ベースキャンプ 大阪',
    category: 'アウトドア',
    rating: 4.5,
    description: 'キャンプ用品から登山ギアまで。本格派も納得のセレクトショップ。',
    address: '大阪府大阪市北区 1-5-8',
    image: 'https://images.unsplash.com/photo-1478131143081-80f7f84ca84d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    badge: null
  }
]);
</script>

<style scoped>
/* ユーティリティクラス（Vuetifyのクラスでカバーしきれない微調整） */
.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }
.gap-4 { gap: 16px; }

.hover-card:hover {
  border-color: rgb(var(--v-theme-primary)) !important;
  cursor: pointer;
}

.hover-primary:hover {
  color: rgb(var(--v-theme-primary)) !important;
  background-color: rgb(var(--v-theme-primary)) !important;
  color: white !important;
}

/* リンクホバー時のテキスト色変更（背景なし） */
a.hover-primary:hover {
  background-color: transparent !important;
  color: rgb(var(--v-theme-primary)) !important;
}

.lh-tight {
  line-height: 1.2;
}

/* パルスアニメーション */
@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(236, 19, 19, 0.7); }
  70% { box-shadow: 0 0 0 20px rgba(236, 19, 19, 0); }
  100% { box-shadow: 0 0 0 0 rgba(236, 19, 19, 0); }
}

.pulse-animation {
  animation: pulse 2s infinite;
}

/* 行数制限（line-clamp） */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.grayscale {
  filter: grayscale(100%);
  transition: filter 0.5s;
}
.group:hover .grayscale {
  filter: grayscale(0%);
}
</style>